import { supertypeClass, property, remote } from '@haventech/amorphic';
import { AppController } from '@hit/amorphic-types';
import { Controller } from '../Controller';
import { Agent } from '../../../../common/js/customer/Agent';
import { Utils } from '../../../../common/js/Utils';
import { CommonUtils } from '../../../../common/js/static/CommonUtils';

import { BaseQuote } from '../../../../common/js/quote/BaseQuote';
import { Applicant } from '../../../../common/js/customer/Applicant';
import { Customer } from '../../../../common/js/customer/Customer';
import { Entitlement } from '../../../../common/js/agent/UserPrincipal';
import { SafeInviteInfo } from '../../../../common/js/customer/SafeInviteInfo';
import { CoverpathCustomer } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { SourceOfFunds } from '../../../../common/js/customer/SourceOfFunds';
import { serverOnly } from '../../../../common/js/helpers';
import { CoverpathPolicy } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { InviteErrors } from '../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../../common/js/logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../../../../common/js/logger/UILoggerSingleton';

export enum SessionMismatchType {
  EMAIL_MISMATCH = 'EMAIL_MISMATCH',
  ID_MISMATCH = 'ID_MISMATCH',
}

@supertypeClass({ toClient: true, toServer: true })
export class InviteController extends AppController {
  @property({ getType: () => Controller })
  controller: Controller;

  @property({ isLocal: true })
  private safeInviteInfo: SafeInviteInfo;

  protected previouslyProcessedCode;

  constructor() {
    super();
  }

  static new(parentController) {
    return new InviteController().init(parentController);
  }

  init(parentController) {
    this.controller = parentController;
    return this;
  }

  @remote({ on: 'server' })
  async reprocessInvite(forceLoadAll?: boolean) {
    await this.processInviteCode(this.previouslyProcessedCode, forceLoadAll);
  }

  @remote({ on: 'server' })
  async getSafeInviteInfo(): Promise<SafeInviteInfo> {
    return this.safeInviteInfo;
  }

  @remote({ on: 'server' })
  async processInviteCode(invitationCode, forceLoadAll?: boolean): Promise<void> {
    const loadAll = this.controller.isLoggedIn() || (!!forceLoadAll && forceLoadAll);
    this.previouslyProcessedCode = invitationCode;
    LoggerSingleton.getInstance().info(
      {
        module: 'inviteController',
        activity: 'processInviteCode',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
        data: {
          inviteCode: invitationCode,
        },
      },
      `Processing invitation code ${invitationCode}`
    );

    // Since some share links had spaces which are being encoded, decode the code and update accordingly.
    invitationCode = decodeURI(invitationCode).replace(/\s/g, '-');
    try {
      let advisorQuote;

      if (loadAll) {
        advisorQuote = await BaseQuote.persistorFetchByQuery({ invite: invitationCode }, { fetch: { customer: true } });
      } else {
        advisorQuote = await BaseQuote.persistorFetchByQuery(
          { invite: invitationCode },
          {
            fetch: {
              customer: { fetch: { applications: false } },
              application: {
                fetch: { policies: false },
              },
            },
          }
        );
      }

      if (advisorQuote && advisorQuote.length) {
        const customer = advisorQuote[0].customer;
        customer.application = advisorQuote[0].application;

        this.verifyInvitationCustomer(customer);

        customer.insuranceQuotes = [];
        customer.insuranceQuotes.push(customer.application.quote);
        this.controller.email = customer.email;
        this.controller.firstName = customer.firstName;
        this.controller.lastName = customer.lastName;
        this.controller.customer = customer;

        let safeQuote;
        if (loadAll) {
          safeQuote = advisorQuote;
        } else {
          safeQuote = await BaseQuote.persistorFetchByQuery(
            { invite: invitationCode },
            { fetch: { customer: true, application: true } }
          );
        }
        const safeCustomer = safeQuote[0].customer as CoverpathCustomer;
        safeCustomer.application = safeQuote[0].application;

        // Make sure old sent links work with source of funds section
        if (!safeCustomer.application.sourceOfFunds) {
          safeCustomer.application.sourceOfFunds = SourceOfFunds.new(safeCustomer.application);
        }

        const currTime = Utils.now();
        const primaryPolicy = safeCustomer.application.primaryPolicy;
        primaryPolicy.linkOpenedDate = currTime;
        primaryPolicy.policyUpdatedTime = currTime;
        await safeCustomer.application.primaryPolicy.persistSave();

        this.safeInviteInfo = new SafeInviteInfo(safeQuote);
      } else {
        LoggerSingleton.getInstance().warn(
          {
            module: 'inviteController',
            function: 'processInviteCode',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
            data: {
              inviteCode: invitationCode,
            },
          },
          `No advisor quote found for invitation code ${invitationCode}`
        );
        throw Error('no advisor quote found for this invite link');
      }
    } catch (e) {
      if (e.message === InviteErrors.SESSION_COLLISION) {
        throw e.message;
      } else {
        const err = {
          name: e?.name,
          message: e?.message,
          stack: e?.stack,
          data: {
            inviteCode: invitationCode,
          },
        };
        LoggerSingleton.getInstance().error(
          {
            module: 'inviteController',
            function: 'processInviteCode',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
            error: err,
          },
          InviteErrors.GENERIC
        );
      }
    }
  }

  private verifyInvitationCustomer(invitationCustomer: Customer): void {
    const customersExist = invitationCustomer && this.controller.customer;
    if (!customersExist) {
      return;
    }
    if (this.controller.customer.email && invitationCustomer.email !== this.controller.customer.email) {
      this.throwSessionCollisionError(invitationCustomer, SessionMismatchType.EMAIL_MISMATCH);
    } else if (this.controller.customer._id && invitationCustomer._id !== this.controller.customer._id) {
      this.throwSessionCollisionError(invitationCustomer, SessionMismatchType.ID_MISMATCH);
    }
  }

  private throwSessionCollisionError(invitationCustomer: Customer, mismatchType: SessionMismatchType): void {
    UILoggerSingleton.getInstance().error(
      {
        module: 'inviteController',
        function: 'verifyInvitationCustomer',
        category: UICategory.request,
        context: {
          referenceType: 'Customer',
          referenceId: this.controller.customer._id,
        },
        error: {
          isHumanRelated: true,
          data: {
            invitationCustomerId: invitationCustomer._id,
            sessionCustomer: this.controller.customer._id,
            mismatchType,
          },
        },
      },
      `Attempt to claim advisor code while another session was logged in`
    );

    throw Error(InviteErrors.SESSION_COLLISION);
  }

  /**
   * Validate the logged in user's AMC email against the loaded quote's email set by the advisor
   *
   * This function will throw if the emails do not match and a quote email exists
   */
  validateSessionEmailAgainstQuote(): void {
    // access the currently loaded quote's email if there is one
    let quoteEmail: string;
    if (
      this.controller &&
      this.controller.customer &&
      this.controller.customer.application &&
      this.controller.customer.application.quote
    ) {
      quoteEmail =
        this.controller.customer.application.quote.customer &&
        this.controller.customer.application.quote.customer.email;
    }

    // ensure the AMC email matches the email on the quote's customer
    if (quoteEmail && quoteEmail !== this.controller.email) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'InviteController',
          function: 'validateSessionEmailAgainstQuote',
          category: UICategory.request,
          data: {
            auth0Id: this.controller && this.controller.auth0userId,
          },
        },
        'The user authenticated with an email that does not match the email on the AAA quote; throwing.'
      );

      throw {
        code: InviteErrors.AAA_QUOTE_EMAIL_MISMATCH,
      };
    }
  }

  @remote({ on: 'server' })
  processAgentReferralCode(agentReferralCode): Promise<boolean> {
    LoggerSingleton.getInstance().info(
      {
        module: 'InviteController',
        function: 'processAgentReferralCode',
        category: Category.request,
        context: {
          piiLevel: PiiLevelEnum.nonPII,
        },
      },
      `Processing agent code ${agentReferralCode}`
    );

    let agent: Agent;
    const clientBaseTermQuote = this.controller.customer.primaryQuote;

    this.controller.resetCustomer();
    return new Promise<boolean>((resolve, reject) => {
      if (!agentReferralCode.match(/[\w-]*/)) {
        LoggerSingleton.getInstance().warn(
          {
            module: 'InviteController',
            function: 'processAgentReferralCode',
            category: Category.request,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
          },
          `Referral code ${agentReferralCode} is not in expected format; rejecting`
        );

        reject(false);
      }

      Agent.persistorFetchByQuery((knex) => knex.where('genericLink', 'like', `%${agentReferralCode}%`), {
        session: this.amorphic,
      })
        .then((agentResultSet: Agent) => {
          agent = agentResultSet[0];
        })
        .then(() => this.controller.customer.createPolicy())
        .then(() => this.controller.customer.application.addPolicyFollowers(agent, Entitlement.AGENT))
        .then(() => agent.asPolicyAgent(this.controller.customer.application.primaryPolicy))
        .then((policyAgent) => (this.controller.customer.application.primaryPolicy.primaryPolicyAgent = policyAgent))
        .then(() => {
          const insured: Applicant = Applicant.new(this.controller.customer);
          this.controller.customer.application.primaryPolicy.insured = insured;
        })
        .then(() => resolve(true))
        .catch((e) => {
          const err = {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            data: {
              agentReferralCode: agentReferralCode,
            },
          };
          LoggerSingleton.getInstance().error(
            {
              module: 'InviteController',
              function: 'processAgentReferralCode',
              category: Category.request,
              context: {
                piiLevel: PiiLevelEnum.nonPII,
              },
              error: err,
            },
            'Something went wrong setting up customer for generic link'
          );
          reject(false);
        });
    });
  }
}
